import {LoaderFunctionArgs} from "react-router";
import React, {useState} from "react";
import {useLoaderData} from "react-router-dom";
import {LoaderData} from "../../types";
import {ServiceName} from "../service/Service";
import {Box, Button, CircularProgress, Fab, Snackbar, ToggleButton, ToggleButtonGroup} from "@mui/material";
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { authenticatedRequest } from "../authenticate/authenticatedRequest";
import { Add } from "@mui/icons-material";
import {Full, Tabbed} from "./site-list";
import {applySiteRecordsToWebsites, sitesFromListResponse} from "./site-list/mergeWebsites";

const PLATFORM_INGRESS_PATTERN = process.env.REACT_APP_PLATFORM_INGRESS_PATTERN || "rezfusion.com";

interface SiteDetail {
    id: string;
    name: string;
    service: string;
    canonicalHostname: string;
    hostnames: string[];
    brandSlug?: string;
    isProd?: boolean;
    goLiveDate?: string | null;
    createdDate?: string | null;
}

const isRezfusionDomain = (hostname: string): boolean => {
    if (!hostname) return false;
    const pattern = PLATFORM_INGRESS_PATTERN.toLowerCase();
    const h = hostname.toLowerCase();
    return h === pattern || h.endsWith("." + pattern);
};

const exportProductionSitesCsv = (sites: SiteDetail[]): void => {
    const headers = ["id", "name", "canonicalHostname", "hostnames", "service", "brandSlug", "goLiveDate", "createdDate"];
    const escape = (v: string) => (v.includes(",") || v.includes('"') || v.includes("\n") ? `"${String(v).replace(/"/g, '""')}"` : v);
    const rows = sites.map((s) =>
        [
            s.id,
            s.name,
            s.canonicalHostname,
            (s.hostnames || []).join("; "),
            s.service,
            s.brandSlug ?? "",
            s.goLiveDate ?? "",
            s.createdDate ?? "",
        ].map(escape).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `production-sites-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

export interface IngressSummary {
    id: string;
    created: string;
    service: ServiceName,
    hostnames: string[],
    isProd?: boolean,
}

async function fetchAllSiteRecords(base: string): Promise<SiteDetail[]> {
    const response = await authenticatedRequest(`${base}/api/sites`);
    if (!response.ok) {
        return [];
    }
    try {
        return sitesFromListResponse(await response.json()) as SiteDetail[];
    } catch {
        return [];
    }
}

export async function ingresses({ params }: LoaderFunctionArgs): Promise<IngressSummary[]> {
    const base = process.env.REACT_APP_PROXY_SERVER || "";
    const [clusterRows, sites] = await Promise.all([
        authenticatedRequest(`${base}/websites`).then((r) => r.json()) as Promise<IngressSummary[]>,
        fetchAllSiteRecords(base),
    ]);
    return applySiteRecordsToWebsites(clusterRows, sites);
}
type ChangeHandler = (event: React.MouseEvent<HTMLElement>, newValue: LayoutTypes) => void
interface VerticalToggleButtonProps {
    view: LayoutTypes,
    handleChange: ChangeHandler
}
const VerticalToggleButtons = (props: VerticalToggleButtonProps): React.JSX.Element => {
    const {
        view,
        handleChange
    } = props;

    return (
        <ToggleButtonGroup
            orientation="vertical"
            value={view}
            exclusive
            onChange={handleChange}
        >
            <ToggleButton value="list" aria-label="list">
                <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="tabbed" aria-label="tabbed">
                <ViewModuleIcon />
            </ToggleButton>
        </ToggleButtonGroup>
    );
}

type LayoutTypes = "tabbed" | "list";
export const Ingresses = (): React.JSX.Element => {

    const [layout, setLayout] = React.useState<LayoutTypes>("tabbed");
    const [exporting, setExporting] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({ open: false, message: "" });
    const ingressesData = useLoaderData() as LoaderData<typeof ingresses>;

    const handleExportProductionSites = React.useCallback(async () => {
        if (!ingressesData?.length) return;
        setExporting(true);
        const base = process.env.REACT_APP_PROXY_SERVER || "";
        try {
            const sites = await fetchAllSiteRecords(base);
            const productionCustom = sites.filter(
                (s) => s.isProd === true && !isRezfusionDomain(s.canonicalHostname || "")
            );
            exportProductionSitesCsv(productionCustom);
            setSnackbar({ open: true, message: `Exported ${productionCustom.length} production site(s).` });
        } catch {
            setSnackbar({ open: true, message: "Export failed. Please try again." });
        } finally {
            setExporting(false);
        }
    }, [ingressesData]);

    if(!ingressesData) {
        return <CircularProgress color="primary"/>
    }

    const output = layout === "tabbed" ? <Tabbed ingressesData={ingressesData} /> : <Full ingressesData={ingressesData} />;

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: 850, margin: "0 auto", px: 2, pb: 2, boxSizing: "border-box" }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                        onClick={handleExportProductionSites}
                        disabled={exporting}
                        aria-label="Export production sites (excludes rezfusion.com domains)"
                    >
                        {exporting ? "Exporting…" : "Export production sites"}
                    </Button>
                </Box>
                {output}
            </Box>
            <div style={{ position: "absolute", left: "95%", bottom: "25px" }}>
                <VerticalToggleButtons view={layout} handleChange={(event, newValue: LayoutTypes) => {
                    setLayout(newValue)
                }}/>
            </div>
            <Fab  sx={{ position: "absolute", left: "90%", bottom: "25px" }} className="contrast" color="primary" aria-label="add" href="/sites/create">
                <Add />
            </Fab>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </>
    );
}